import dayjs from 'dayjs'

/**
 * Formats a value the API sends as a decimal string as US currency.
 * @param {string|number} value
 * @returns formatted amount, e.g. `$22.00`
 */
export const formatCurrency = (value) =>
  (parseFloat(value) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

/**
 * Line items as the receipt renders them, with the extended cost worked out.
 *
 * Mirrors the emailed receipt, which multiplies out the same way.
 * @param {object} transaction
 * @returns array of line items with a `cost` added
 */
export const getReceiptLineItems = (transaction) => {
  const items = transaction?.lineItems?.lineItem || []

  return items.map((item) => ({
    ...item,
    cost: (parseFloat(item.unitPrice) || 0) * (parseInt(item.quantity, 10) || 0),
  }))
}

/**
 * When the purchase was made. Authorize.net stamps `submitTime`; a zero-total purchase
 * never reaches the gateway, so it falls back to when we recorded the transaction.
 * @param {object} transaction
 * @returns dayjs date
 */
export const getPurchaseDate = (transaction) =>
  dayjs(transaction.submitTime || transaction.createdAt)

/**
 * Whether any of this purchase has been given back.
 *
 * Reads the API's derived status rather than the length of `refunds`, so it stays right
 * for a purchase reversed in ways that record no items — a void, or a refund issued
 * straight in the gateway.
 * @param {object} transaction
 * @returns boolean
 */
export const isRefunded = (transaction) =>
  Boolean(transaction?.refundStatus) && transaction.refundStatus !== 'None'

/**
 * What the exhibitor is out of pocket once refunds come off.
 *
 * Falls back to the charged amount, so a transaction from before the API reported refund
 * state still shows a total rather than $0.00.
 * @param {object} transaction
 * @returns number
 */
export const getNetAmount = (transaction) => {
  const net = parseFloat(transaction?.netAmount)

  return Number.isNaN(net) ? parseFloat(transaction?.authorizedAmount) || 0 : net
}

/**
 * The rows a receipt lists under the charge, one per item a refund covered.
 *
 * A refund names what it covered, so the receipt can say which license came back rather
 * than only that money did. A refund with no items is one nothing recorded the shape of
 * — a void, or one issued outside the portal — and gets a single row for its amount.
 *
 * Ordered oldest first by the API, so a purchase refunded an item at a time reads down
 * the page in the order the refunds were issued.
 * @param {object} transaction
 * @returns array of `{ key, label, amount, date }`, where `date` is a dayjs date or null
 */
export const getRefundRows = (transaction) => {
  if (!isRefunded(transaction)) return []

  const refunds = transaction?.refunds || []

  return refunds.flatMap((refund) => {
    const date = refund.createdAt ? dayjs(refund.createdAt) : null
    const items = refund.items || []

    if (!items.length) {
      return [
        {
          key: refund.id,
          label: refund.isVoid ? 'Payment Voided' : 'Refund Issued',
          amount: Math.abs(parseFloat(refund.amount) || 0),
          date,
        },
      ]
    }

    return items.map((item) => ({
      key: item.id,
      label: item.description,
      amount: Math.abs(parseFloat(item.amount) || 0),
      date,
    }))
  })
}

/**
 * Short summary of what a purchase bought, for the purchase history table.
 * @param {object} transaction
 * @returns e.g. `1 License, 2 Device Rentals`
 */
export const summarizePurchase = (transaction) => {
  const parts = []

  if (transaction.numLicenses > 0) {
    parts.push(`${transaction.numLicenses} License${transaction.numLicenses === 1 ? '' : 's'}`)
  }

  if (transaction.numRentalDevices > 0) {
    parts.push(
      `${transaction.numRentalDevices} Device Rental${
        transaction.numRentalDevices === 1 ? '' : 's'
      }`,
    )
  }

  // Older transactions predate the per-item counts, so fall back to naming the line items
  if (!parts.length) {
    return getReceiptLineItems(transaction)
      .map((item) => `${item.quantity} ${item.name}`)
      .join(', ')
  }

  return parts.join(', ')
}
